/**
 * One call the AI placed, as the results page lists it: who, when, which
 * number rang, what came of it and what a person should do next.
 *
 * A test call says so twice, with a pill and with the company's real number
 * beside the test number that rang, because a result read without that
 * context is a company that seems to have agreed to a meeting on the owner's
 * own phone. The words of the call (summary, next step, contact) are left out
 * when the server withholds them, and the row says why.
 *
 * An alert call (ring R9) carries the defect it was about: a pill with the
 * defect, and under the details when it was first seen, when the recheck right
 * before the call saw it, and the sentence the AI said about it. That is what
 * a person needs when the company says the site was never broken.
 */
import { Link } from "react-router-dom";
import { Ban, Brain, Building2, CalendarClock, Headphones, Mail, ShieldAlert, User } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { alertClassTone, alertFindingKey } from "../../../../lib/aiAlert";
import { dispositionKey, dispositionTone, resultOptsOut, type AiOutboundResultRow } from "../../../../lib/aiOutboundResults";
import { brainPath } from "../../../../lib/aiTestCallPreview";
import { formatPhone } from "../../../../lib/phone";
import { StatusPill, fmtDateTime } from "../../../ui";

export function ResultRow({ row }: { row: AiOutboundResultRow }) {
  const t = useT();
  const d = row.details;
  const name = row.companyName || row.domain || formatPhone(row.toNumber);
  const alert = row.alert ?? null;
  const findingKey = alert ? alertFindingKey(alert.findingId) : null;

  return (
    <li className="space-y-2 px-4 py-3">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="min-w-0 font-medium">{name}</span>
        <StatusPill tone={dispositionTone(row.disposition)}>
          {t(`aiCalls.disposition.${dispositionKey(row.disposition)}`)}
        </StatusPill>
        {row.test ? <StatusPill tone="info">{t("aiCalls.test")}</StatusPill> : null}
        {alert ? (
          <StatusPill tone={alertClassTone(alert.class)}>
            <ShieldAlert className="size-3" />
            {t("aiCalls.alert.pill", { finding: findingKey ? t(findingKey) : alert.findingId })}
          </StatusPill>
        ) : null}
        {resultOptsOut(row) ? (
          <StatusPill tone="lt">
            <Ban className="size-3" />
            {t(row.test ? "aiCalls.optOutTest" : "aiCalls.optOut")}
          </StatusPill>
        ) : null}
        <span className="crm-sub ml-auto text-xs">{fmtDateTime(row.startedAt)}</span>
      </div>

      <div className="crm-sub flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span>
          {row.test && row.intendedNumber
            ? t("aiCalls.testNumbers", { rang: formatPhone(row.toNumber), real: formatPhone(row.intendedNumber) })
            : formatPhone(row.toNumber)}
        </span>
        {row.durationSec !== null ? <span>{t("aiCalls.duration", { sec: row.durationSec })}</span> : null}
        {row.source === "fallback" ? <span>{t(`aiCalls.fallback.${row.fallbackReason ?? "model_failed"}`)}</span> : null}
      </div>

      {d ? (
        <div className="space-y-1 text-sm">
          {d.summary ? <p>{d.summary}</p> : null}
          {d.nextStep ? <p className="font-medium">{t("aiCalls.nextStep", { step: d.nextStep })}</p> : null}
          <div className="crm-sub flex flex-wrap gap-x-4 gap-y-1 text-xs">
            {d.callbackAt ? (
              <span className="inline-flex items-center gap-1">
                <CalendarClock className="size-3.5" />
                {t("aiCalls.callbackAt", { at: fmtDateTime(d.callbackAt) })}
              </span>
            ) : null}
            {d.contactName ? (
              <span className="inline-flex items-center gap-1">
                <User className="size-3.5" />
                {d.contactName}
              </span>
            ) : null}
            {d.email ? (
              <span className="inline-flex items-center gap-1">
                <Mail className="size-3.5" />
                {d.email}
              </span>
            ) : null}
          </div>
        </div>
      ) : row.withheld ? (
        <p className="crm-sub text-xs">{t("aiCalls.withheld")}</p>
      ) : null}

      {alert ? (
        <div className="space-y-0.5 text-xs">
          <p className="crm-sub">
            {t("aiCalls.alert.firstSeen", { at: fmtDateTime(alert.firstSeenAt) })}
            {" · "}
            {t("aiCalls.alert.verifiedAt", { at: fmtDateTime(alert.verifiedAt) })}
          </p>
          {alert.spokenText ? <p>{t("aiCalls.alert.spoken", { text: alert.spokenText })}</p> : null}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <Link to={`/calls/${row.id}`} className="inline-flex items-center gap-1 text-primary hover:underline">
          <Headphones className="size-3.5" />
          {t(row.hasRecording ? "aiCalls.openCallRecorded" : "aiCalls.openCall")}
        </Link>
        {row.domain ? (
          <Link to={brainPath(row.domain)} className="inline-flex items-center gap-1 text-primary hover:underline">
            <Brain className="size-3.5" />
            {t("aiCalls.openBrain")}
          </Link>
        ) : null}
        {row.regCode ? (
          <Link to={`/ariregister/${row.regCode}`} className="inline-flex items-center gap-1 text-primary hover:underline">
            <Building2 className="size-3.5" />
            {t("aiCalls.openCompany")}
          </Link>
        ) : null}
      </div>
    </li>
  );
}
